import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { batchedQuery } from '@/lib/batched-query';

// ─── Types ──────────────────────────────────────────────────────────────

export type ReportReadiness = 'ready' | 'blocked';

export type BlockedReason =
  | 'not_subscribed'
  | 'subscription_disabled'
  | 'missing_battery_snapshot'
  | 'low_battery_topology_confidence'
  | 'topology_review_required'
  | 'insufficient_telemetry'
  | 'missing_electricity_rate'
  | null;

export interface ReportReadinessRow {
  property_id: string;
  property_name: string;
  provider_type: string;
  system_count: number;
  // Subscription
  subscription_id: string | null;
  subscribed: boolean;
  enabled: boolean;
  send_day_of_month: number | null;
  last_sent_at: string | null;
  // Telemetry
  days_with_data: number;
  latest_telemetry_at: string | null;
  // Battery
  has_battery: boolean;
  battery_kwh_total: number | null;
  has_battery_snapshot: boolean;
  last_snapshot_date: string | null;
  // Topology (multi-inverter only)
  is_multi_inverter: boolean;
  topology_confidence: string | null;
  topology_review_required: boolean;
  // Forecast
  has_electricity_rate: boolean;
  // Derived
  readiness: ReportReadiness;
  blocked_reason: BlockedReason;
}

// ─── Readiness resolver (pure, testable) ────────────────────────────────

export function resolveReportReadiness(input: {
  subscribed: boolean;
  enabled: boolean;
  days_with_data: number;
  has_battery: boolean;
  has_battery_snapshot: boolean;
  is_multi_inverter: boolean;
  topology_confidence: string | null;
  topology_review_required: boolean;
  has_electricity_rate: boolean;
}): { readiness: ReportReadiness; blocked_reason: BlockedReason } {
  // Gate 1: subscription
  if (!input.subscribed) {
    return { readiness: 'blocked', blocked_reason: 'not_subscribed' };
  }
  if (!input.enabled) {
    return { readiness: 'blocked', blocked_reason: 'subscription_disabled' };
  }

  // Gate 2: minimum telemetry (need at least 7 days to produce a meaningful report)
  if (input.days_with_data < 7) {
    return { readiness: 'blocked', blocked_reason: 'insufficient_telemetry' };
  }

  // Gate 3: battery properties need a snapshot for capacity reporting
  if (input.has_battery && !input.has_battery_snapshot) {
    return { readiness: 'blocked', blocked_reason: 'missing_battery_snapshot' };
  }

  // Gate 4: multi-inverter topology confidence
  if (input.is_multi_inverter && input.topology_review_required) {
    return { readiness: 'blocked', blocked_reason: 'topology_review_required' };
  }
  if (input.is_multi_inverter && input.topology_confidence === 'low') {
    return { readiness: 'blocked', blocked_reason: 'low_battery_topology_confidence' };
  }

  // Gate 5: savings calculation needs an electricity rate
  if (!input.has_electricity_rate) {
    return { readiness: 'blocked', blocked_reason: 'missing_electricity_rate' };
  }

  return { readiness: 'ready', blocked_reason: null };
}

// ─── Fetch ──────────────────────────────────────────────────────────────

async function fetchReportReadiness(): Promise<ReportReadinessRow[]> {
  // 1. All properties with active service provider
  const { data: properties, error: propErr } = await supabase
    .from('properties')
    .select('id, name, battery_kwh_total, electricity_rate_cents_per_kwh, service_provider_org_id')
    .not('service_provider_org_id', 'is', null);

  if (propErr) throw propErr;
  if (!properties?.length) return [];

  const propIds = properties.map((p) => p.id);

  // 2. System counts per property — batched for scale safety
  const systems = await batchedQuery(propIds, async (chunk) => {
    const { data } = await supabase
      .from('energy_systems')
      .select('id, property_id')
      .in('property_id', chunk);
    return data ?? [];
  });

  const systemCountByProp = new Map<string, number>();
  for (const s of systems) {
    systemCountByProp.set(s.property_id, (systemCountByProp.get(s.property_id) ?? 0) + 1);
  }

  // 3. Plants (for battery info + topology) — batched
  const plants = await batchedQuery(propIds, async (chunk) => {
    const { data } = await supabase
      .from('plants')
      .select('id, property_id, provider_type, battery_kwh_configured, battery_topology_override')
      .in('property_id', chunk);
    return data ?? [];
  });

  const plantByProp = new Map<string, (typeof plants)[number]>();
  for (const p of plants) {
    plantByProp.set(p.property_id, p);
  }

  // 4. Subscriptions — batched
  const subs = await batchedQuery(propIds, async (chunk) => {
    const { data } = await supabase
      .from('customer_report_subscriptions')
      .select('id, property_id, enabled, send_day_of_month, last_sent_at')
      .in('property_id', chunk);
    return data ?? [];
  });

  const subByProp = new Map(subs.map((s) => [s.property_id, s]));

  // 5. Battery health snapshots (latest per property via plant) — batched
  const plantIds = plants.map((p) => p.id);
  const snapshots = await batchedQuery(plantIds, async (chunk) => {
    const { data } = await supabase
      .from('battery_health_snapshots')
      .select('plant_id, created_at, computed_capacity_kwh')
      .in('plant_id', chunk)
      .not('computed_capacity_kwh', 'is', null)
      .order('created_at', { ascending: false });
    return data ?? [];
  });

  const snapshotByPlant = new Map<string, { created_at: string }>();
  for (const s of snapshots) {
    if (!snapshotByPlant.has(s.plant_id)) {
      snapshotByPlant.set(s.plant_id, { created_at: s.created_at });
    }
  }

  // 6. Telemetry recency — use daily_energy_summary to count days with data (last 60 days) — batched
  const sixtyDaysAgo = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const dailySummaries = await batchedQuery(propIds, async (chunk) => {
    const { data } = await supabase
      .from('daily_energy_summary')
      .select('property_id, date')
      .in('property_id', chunk)
      .gte('date', sixtyDaysAgo);
    return data ?? [];
  });

  const daysWithDataByProp = new Map<string, number>();
  const latestDateByProp = new Map<string, string>();
  for (const d of dailySummaries) {
    daysWithDataByProp.set(d.property_id, (daysWithDataByProp.get(d.property_id) ?? 0) + 1);
    const existing = latestDateByProp.get(d.property_id);
    if (!existing || d.date > existing) {
      latestDateByProp.set(d.property_id, d.date);
    }
  }

  // 7. Topology stats for multi-inverter (reuse existing RPC)
  const multiInverterPlantIds = plantIds.filter((pid) => {
    const plant = plants.find((p) => p.id === pid);
    if (!plant) return false;
    return (systemCountByProp.get(plant.property_id) ?? 1) >= 2;
  });

  let topologyMap = new Map<string, { confidence: string; review_required: boolean }>();
  if (multiInverterPlantIds.length > 0) {
    const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString();
    const { data: topoStats } = await supabase.rpc('admin_battery_topology_stats', {
      p_plant_ids: multiInverterPlantIds,
      p_since: threeDaysAgo,
    });

    // Classify each (simplified — mirrors useAdminBatteryTopology logic)
    for (const pid of multiInverterPlantIds) {
      const plant = plants.find((p) => p.id === pid);
      const override = plant?.battery_topology_override as string | null;
      const stats = (topoStats as Array<Record<string, unknown>> ?? []).find(
        (s) => s.plant_id === pid,
      );
      const systemCount = plant ? (systemCountByProp.get(plant.property_id) ?? 2) : 2;

      if (override === 'shared_bank' || override === 'independent_banks') {
        topologyMap.set(pid, { confidence: 'high', review_required: false });
      } else if (!stats || Number(stats.overlapping_buckets ?? 0) < 20) {
        topologyMap.set(pid, { confidence: 'low', review_required: true });
      } else {
        const avg = Number(stats.avg_soc_delta ?? 0);
        const max = Number(stats.max_soc_delta ?? 0);
        const p95 = Number(stats.p95_soc_delta ?? 0);
        const sustained = Number(stats.sustained_divergence_count ?? 0);
        const buckets = Number(stats.overlapping_buckets ?? 0);

        if (avg <= 1.0 && max <= 3.0) {
          topologyMap.set(pid, {
            confidence: buckets >= 50 ? 'high' : 'medium',
            review_required: false,
          });
        } else if (avg >= 5.0 || p95 >= 10.0 || sustained >= 3) {
          const approximate = systemCount > 2;
          topologyMap.set(pid, {
            confidence: approximate ? 'medium' : (buckets >= 50 ? 'high' : 'medium'),
            review_required: approximate,
          });
        } else {
          topologyMap.set(pid, { confidence: 'low', review_required: true });
        }
      }
    }
  }

  // 8. Build rows
  return properties.map((prop) => {
    const sub = subByProp.get(prop.id);
    const plant = plantByProp.get(prop.id);
    const systemCount = systemCountByProp.get(prop.id) ?? 1;
    const hasBattery = (prop.battery_kwh_total ?? 0) > 0;
    const snapshot = plant ? snapshotByPlant.get(plant.id) : undefined;
    const isMulti = systemCount >= 2 && hasBattery;
    const topo = plant ? topologyMap.get(plant.id) : undefined;
    const daysWithData = daysWithDataByProp.get(prop.id) ?? 0;
    const latestDate = latestDateByProp.get(prop.id) ?? null;

    const input = {
      subscribed: !!sub,
      enabled: sub?.enabled ?? false,
      days_with_data: daysWithData,
      has_battery: hasBattery,
      has_battery_snapshot: !!snapshot,
      is_multi_inverter: isMulti,
      topology_confidence: topo?.confidence ?? null,
      topology_review_required: topo?.review_required ?? false,
      has_electricity_rate: (prop.electricity_rate_cents_per_kwh ?? 0) > 0,
    };

    const { readiness, blocked_reason } = resolveReportReadiness(input);

    return {
      property_id: prop.id,
      property_name: prop.name ?? 'Unknown',
      provider_type: plant?.provider_type ?? 'unknown',
      system_count: systemCount,
      subscription_id: sub?.id ?? null,
      subscribed: !!sub,
      enabled: sub?.enabled ?? false,
      send_day_of_month: sub?.send_day_of_month ?? null,
      last_sent_at: sub?.last_sent_at ?? null,
      days_with_data: daysWithData,
      latest_telemetry_at: latestDate,
      has_battery: hasBattery,
      battery_kwh_total: prop.battery_kwh_total,
      has_battery_snapshot: !!snapshot,
      last_snapshot_date: snapshot?.created_at ?? null,
      is_multi_inverter: isMulti,
      topology_confidence: topo?.confidence ?? null,
      topology_review_required: topo?.review_required ?? false,
      has_electricity_rate: (prop.electricity_rate_cents_per_kwh ?? 0) > 0,
      readiness,
      blocked_reason,
    };
  }).sort((a, b) => {
    // Subscribed + blocked first, then subscribed + ready, then unsubscribed
    if (a.subscribed !== b.subscribed) return a.subscribed ? -1 : 1;
    if (a.subscribed && b.subscribed) {
      if (a.readiness !== b.readiness) return a.readiness === 'blocked' ? -1 : 1;
    }
    return a.property_name.localeCompare(b.property_name);
  });
}

// ─── Hook ───────────────────────────────────────────────────────────────

export function useAdminReportReadiness(enabled: boolean) {
  return useQuery({
    queryKey: ['admin-report-readiness'],
    queryFn: fetchReportReadiness,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}
